import { MIN_STAIR_WIDTH } from '../../../../../constants/limits';
import BlockType from '../../../../../enums/blockType';
import EdgeLabel from '../../../../../enums/edgeLabel';
import EdgeType from '../../../../../enums/edgeType';
import { Block, Edge } from '../../../../../types/shared/blocks';
import { getOppositeEdge } from './edgeUtils';
import { v4 as uuidv4 } from 'uuid';

function getDefaultLanding(
	parentBlock: Block | null,
	selectedBlockEdge: Edge | undefined
) {
	if (!parentBlock || !selectedBlockEdge) return;

	const inferedHeight =
		parentBlock.type == BlockType.STAIR
			? parentBlock.dimensions.endHeight || 0
			: parentBlock?.dimensions?.height || 0;

	const widthDimension =
		selectedBlockEdge.label == EdgeLabel.TOP ||
		selectedBlockEdge.label == EdgeLabel.BOTTOM
			? 'width'
			: 'height';

	const defaultSize = 48;
	const inferedWidth = Math.max(
		parentBlock?.type == BlockType.STAIR
			? widthDimension == 'width'
				? parentBlock.displayDimensions.width
				: parentBlock.displayDimensions.height
			: selectedBlockEdge.length || defaultSize,
		MIN_STAIR_WIDTH
	);

	const depth =
		parentBlock?.type == BlockType.STAIR
			? defaultSize
			: selectedBlockEdge.length || defaultSize;

	const landingId = uuidv4();

	const defaultLanding: Block = {
		id: landingId,
		type: BlockType.LANDING,
		// Position will be set in the reducer
		dimensions: {
			width: inferedWidth,
			depth,
			height: inferedHeight,
		}, // Example dimensions
		displayDimensions: {
			width: widthDimension == 'width' ? inferedWidth : depth,
			height: widthDimension == 'width' ? depth : inferedWidth,
		},
		parentBlock: {
			id: parentBlock?.id,
			type: parentBlock?.type,
		},
		edges: [
			EdgeLabel.TOP,
			EdgeLabel.RIGHT,
			EdgeLabel.BOTTOM,
			EdgeLabel.LEFT,
		].map((edge) => {
			const edgeId = uuidv4();
			const type =
				edge === selectedBlockEdge.label
					? inferedHeight == 0
						? EdgeType.GROUND
						: EdgeType.RAILING
					: edge === getOppositeEdge(selectedBlockEdge.label)
						? EdgeType.BLOCK_ATTACHED
						: EdgeType.RAILING;

			const length =
				edge === selectedBlockEdge.label ||
				edge === getOppositeEdge(selectedBlockEdge.label)
					? inferedWidth
					: depth;

			return {
				id: edgeId,
				label: edge,
				attachedToParent: edge === getOppositeEdge(selectedBlockEdge.label),
				type,
				length,
				segments: [
					{
						id: uuidv4(),
						edgeId: edgeId,
						blockId: landingId,
						length,
						type,
					},
				],
			};
		}),
	};

	return defaultLanding;
}

export function getDefaultLandingV2({
	parentBlockId,
	parentBlockType,
	height,
	blockEdge,
	parentBlockDisplayWidth,
	parentBlockDisplayHeight,
}: {
	parentBlockId: string | undefined;
	parentBlockType: BlockType;
	height: number | undefined;
	blockEdge: Edge | undefined;
	parentBlockDisplayWidth: number;
	parentBlockDisplayHeight: number;
}) {
	if (!parentBlockId || !blockEdge || !height) return;

	const widthDimension =
		blockEdge.label == EdgeLabel.TOP || blockEdge.label == EdgeLabel.BOTTOM
			? 'width'
			: 'height';

	const defaultSize = 48; // 4 feet in inches

	const inferedWidth = Math.max(
		parentBlockType == BlockType.STAIR
			? widthDimension == 'width'
				? parentBlockDisplayWidth
				: parentBlockDisplayHeight
			: blockEdge.length || defaultSize,
		MIN_STAIR_WIDTH
	);

	const depth =
		parentBlockType == BlockType.STAIR
			? defaultSize
			: blockEdge.length || defaultSize;

	const landingId = uuidv4();

	const defaultLanding: Block = {
		id: landingId,
		type: BlockType.LANDING,
		// Position will be set in the reducer
		dimensions: {
			width: inferedWidth,
			depth,
			height: height,
		}, // Example dimensions
		displayDimensions: {
			width: widthDimension == 'width' ? inferedWidth : depth,
			height: widthDimension == 'width' ? depth : inferedWidth,
		},
		parentBlock: {
			id: parentBlockId,
			type: parentBlockType,
		},
		edges: [
			EdgeLabel.TOP,
			EdgeLabel.RIGHT,
			EdgeLabel.BOTTOM,
			EdgeLabel.LEFT,
		].map((edge) => {
			const edgeId = uuidv4();
			const type =
				edge === blockEdge.label
					? height == 0
						? EdgeType.GROUND
						: EdgeType.RAILING
					: edge === getOppositeEdge(blockEdge.label)
						? EdgeType.BLOCK_ATTACHED
						: EdgeType.RAILING;

			const length =
				edge === blockEdge.label || edge === getOppositeEdge(blockEdge.label)
					? inferedWidth
					: depth;

			return {
				id: edgeId,
				label: edge,
				attachedToParent: edge === getOppositeEdge(blockEdge.label),
				type,
				length,
				segments: [
					{
						id: uuidv4(),
						edgeId: edgeId,
						blockId: landingId,
						length,
						type,
					},
				],
			};
		}),
	};

	return defaultLanding;
}

export default getDefaultLanding;
